import React from 'react';

import { convertFieldsToSearchParams, validationOnRequiredAndReturnErrorFields } from 'components/form-multi/utils';
import { amplitudeLogout } from 'components/utils/AmplitudeUtils';
import ServerErrorPopup from 'components/utils/ServerErrorPopup';

import { LOCATION, SPRING_REDIRECT_HEADER } from 'constants/Constants';
import { LOGIN } from 'constants/UrlConstants';

import { getFullEncodeApiUrl, processResponseForCloudFlare } from 'helpers/api';
import { isSoftOrHardServerError } from 'helpers/utils';

import * as Sentry from '@sentry/react';

import { ForbiddenError } from '../components/exceptions/ForbiddenError';

export class FormService {
    #getCanonicalPath;
    #controller;
    #formName;

    //actions
    #reduxAction;
    #action;
    //form
    #reduxValidateForm;
    #validateForm;
    #checkNotVisibleField;

    // handlers
    #beforeRedirect;
    #beforeSubmit;
    #onErrorHandler;
    #onSubmitHandler;

    //reduxFunctions
    #updateMessages;
    #updateAuthPromoBlock;
    #updatePageMessages;
    #updateComplexMessages;
    #addExtras;
    #updateCountryMenu;

    #setFieldError;
    #setNewForm;
    #changeFormField;
    #scrollToField;
    #toggleSpinner;
    #toggleCloudFlareModal;

    //popups
    #popupRef;

    constructor(getCanonicalPath, controller, formName) {
        this.#getCanonicalPath = getCanonicalPath;
        this.#controller = controller;
        this.#formName = formName;
    }

    setPopups(popupRef) {
        this.#popupRef = popupRef;
    }

    setAction(reduxAction, action) {
        this.#reduxAction = reduxAction;
        this.#action = action;
    }

    setFormProps(reduxValidateForm, validateForm, checkNotVisibleField) {
        this.#reduxValidateForm = reduxValidateForm;
        this.#validateForm = validateForm;
        this.#checkNotVisibleField = checkNotVisibleField;
    }

    setAppAction(setFieldError, setNewForm, changeFormField, scrollToField, toggleSpinner, toggleCloudFlareModal) {
        this.#setFieldError = setFieldError;
        this.#setNewForm = setNewForm;
        this.#changeFormField = changeFormField;
        this.#scrollToField = scrollToField;
        this.#toggleSpinner = toggleSpinner;
        this.#toggleCloudFlareModal = toggleCloudFlareModal;
    }

    setHandlers(beforeRedirect, beforeSubmit, onErrorHandler, onSubmitHandler) {
        this.#beforeRedirect = beforeRedirect;
        this.#beforeSubmit = beforeSubmit;
        this.#onErrorHandler = onErrorHandler;
        this.#onSubmitHandler = onSubmitHandler;
    }

    setUpdateRedux(updateMessages, updateAuthPromoBlock, updatePageMessages, updateComplexMessages, addExtras, updateCountryMenu) {
        this.#updateMessages = updateMessages;
        this.#updateAuthPromoBlock = updateAuthPromoBlock;
        this.#updatePageMessages = updatePageMessages;
        this.#updateComplexMessages = updateComplexMessages;
        this.#addExtras = addExtras;
        this.#updateCountryMenu = updateCountryMenu;
    }

    handlerSubmit(e, reduxFields) {
        if (this.#beforeSubmit) {
            this.#beforeSubmit(e);
        }

        if (e && e.preventDefault) {
            e.preventDefault();
        }

        const fields = { ...reduxFields };
        const action = this.getAction();
        if (__isDev__) {
            console.log('handlerSubmit(e) action: ', action);
        }

        if (fields && action) {
            const isValidateForm = typeof this.#reduxValidateForm == 'boolean' ? this.#reduxValidateForm : this.#validateForm;
            const errorFields = isValidateForm
                ? validationOnRequiredAndReturnErrorFields(this.#formName, fields, this.#changeFormField, this.#checkNotVisibleField)
                : [];

            const canMakeRequest = isValidateForm ? errorFields.length === 0 : true;

            if (canMakeRequest) {
                this.#abort();
                this.#toggleSpinner(true);
                this.#submitForm(fields);
            }
        } else {
            return Promise.resolve(`can't submit :( Reason: fields or action are invalid`);
        }
    }

    #abort = () => {
        try {
            this.#controller.current.abort();
        } catch (e) {
            Sentry.captureException(e, {
                level: 'log',
            });
            console.warn(e);
        } finally {
            this.#controller.current = new AbortController();
        }
    };

    getAction = () => (this.#reduxAction ? this.#reduxAction : this.#action);

    #submitForm(fields = {}) {
        let responseCode;
        const options = {
            signal: this.#controller.current.signal,
            method: 'POST',
            mode: 'cors',
            body: this.#getDataForForm(fields, this.#getCanonicalPath),
            credentials: 'include',
        };
        return fetch(getFullEncodeApiUrl(this.getAction()), options)
            .then((response) => {
                responseCode = response.status;
                processResponseForCloudFlare(response, getFullEncodeApiUrl(this.getAction()), options, {
                    toggleCloudFlareModal: this.#toggleCloudFlareModal,
                });
                this.#processResponseForRedirect(response);
                return response.json();
            })
            .then((json) => {
                if (json) {
                    this.#processJson(json, fields, responseCode);
                }
                this.#abort();
            })
            .catch((error) => {
                console.warn(error);
                if (this.#onErrorHandler) {
                    Promise.resolve(this.#onErrorHandler(error)).then(() => this.#toggleSpinner(false));
                } else {
                    this.#toggleSpinner(false);
                    throw error;
                }
            });
    }

    #getDataForForm = (fields = {}, getCanonicalPath = false) => {
        return convertFieldsToSearchParams(fields, getCanonicalPath);
    };

    #processResponseForRedirect = (response) => {
        if (response.status === 403) {
            window.location.replace(LOGIN);
            amplitudeLogout();
            throw new ForbiddenError(`redirect to ${LOGIN}`);
        } else if (response.headers.has(SPRING_REDIRECT_HEADER)) {
            window.location.replace(response.headers.get(SPRING_REDIRECT_HEADER));
            throw new Error(`redirect to ${LOGIN}`);
        } else if (response.headers.has(LOCATION)) {
            window.location.replace(response.headers.get(LOCATION));
            throw new Error(`redirect to ${LOGIN}`);
        }
    };

    #highlightErrorFields(json = {}) {
        if (json.errors) {
            Object.keys(json.errors).forEach((name) => this.#setFieldError(this.#formName, name, json.errors[name]));
        }

        if (json.validateResultErrors) {
            const validateRrrorFields = Object.keys(json.validateResultErrors);
            validateRrrorFields.forEach((name) => this.#setFieldError(this.#formName, name, json.validateResultErrors[name]));
            validateRrrorFields.length > 0 && this.#scrollToField(this.#formName, validateRrrorFields[0]);
        }

        const form = json?.forms && json.forms[this.#formName];
        if (form && form.errors) {
            Object.keys(form.errors).forEach((name) => this.#setFieldError(this.#formName, name, form.errors[name]));
        }
    }

    #updateRedux(json = {}) {
        if (json.messageBodyMessages) {
            this.#updateMessages(json.messageBodyMessages);
        }

        this.#updateAuthPromoBlock(json.authPromoAction);

        if (json.messages) {
            this.#updatePageMessages(json.messages);
        }
        if (json.complexMessages) {
            this.#updateComplexMessages(json.complexMessages);
        }
        if (json.countryMenu) {
            this.#updateCountryMenu(this.#formName, json.countryMenu);
        }
        if (json.extra) {
            this.#addExtras(json.extra);
        }
        const formsNames = Object.keys(json.forms).filter((fn) => this.#formName !== fn);
        if (formsNames?.length > 0) {
            formsNames.forEach((fn) => {
                const newForm = json.forms[fn];
                this.#setNewForm(newForm?.name, newForm?.fields, newForm?.action);
            });
        }
    }

    #processJson = (json = {}, fields = {}, responseCode = 200) => {
        if (json.redirectTo) {
            if (this.#beforeRedirect) {
                this.#beforeRedirect();
            }
            window.location.href = json.redirectTo;
            return;
        }
        if (json.showServerError && !isSoftOrHardServerError(json.extra?.serverErrorCode?.code)) {
            this.#popupRef?.current?.open(
                <ServerErrorPopup popupRef={this.#popupRef} errorText={json.messages?.errorText} buttonClose={json.messages?.buttonClose} />,
            );
        }

        this.#updateRedux(json);
        this.#highlightErrorFields(json);

        if (responseCode === 200 && this.#onSubmitHandler) {
            Promise.resolve(this.#onSubmitHandler(fields, json)).then(() => this.#toggleSpinner(false));
        } else {
            this.#toggleSpinner(false);
        }
    };
}
